import { Injectable, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { User } from '../models/user';
import {
  FilmDrawingLayer,
  FilmDrawingLayerChange,
  FilmDrawingShape,
  FilmLoadRequest,
  FilmMediaCapabilities,
  FilmParticipant,
  FilmPlaybackHeartbeat,
  FilmPlaybackState,
  FilmPointer,
  FilmReadyCount,
  FilmSession,
  FilmSessionRecordingState,
  FilmSessionRole,
  FilmStroke,
} from '../models/film-session';
import { Group } from '../models/annotation';

export interface FilmSocketAck {
  success: boolean;
  message?: string;
}

export interface FilmRoomJoin extends FilmSocketAck {
  role?: FilmSessionRole;
  state?: FilmPlaybackState | null;
  participants?: FilmParticipant[];
  /** Every frame drawn on so far, for a late joiner. */
  drawings?: FilmDrawingLayer[];
  serverNow?: number;
}

export interface FilmLayerAck extends FilmSocketAck, FilmDrawingLayerChange {}

export interface FilmStateAck extends FilmSocketAck {
  state?: FilmPlaybackState;
}

const ACK_TIMEOUT_MS = 8000;

/**
 * The live film session socket: the backend's `/film` namespace, authenticated at handshake with the user's token.
 * It is open while the signed-in user's current team has live film sessions enabled, so invitees hear when a
 * session starts, and the session room uses it for synced playback.
 */
@Injectable({
  providedIn: 'root'
})
export class FilmSocketService implements OnDestroy {

  private socket: Socket = null;
  private connectionKey: string = null;
  private subscriptions = new Subscription();

  private readonly connection = new BehaviorSubject<boolean>(false);
  private readonly sessionLive = new Subject<FilmSession>();
  private readonly sessionEnded = new Subject<FilmSession>();
  private readonly state = new Subject<FilmPlaybackState>();
  private readonly heartbeats = new Subject<FilmPlaybackHeartbeat>();
  private readonly presence = new Subject<{sessionId: string; participants: FilmParticipant[]}>();
  private readonly readyCounts = new Subject<FilmReadyCount>();
  private readonly recalls = new Subject<{sessionId: string; by: string}>();
  private readonly removals = new Subject<{sessionId: string; userid: string}>();
  private readonly mediaCapabilities = new Subject<FilmMediaCapabilities>();
  private readonly floors = new Subject<{sessionId: string; open: boolean}>();
  private readonly layers = new Subject<FilmDrawingLayerChange>();
  private readonly strokes = new Subject<FilmStroke>();
  private readonly pointers = new Subject<FilmPointer>();
  private readonly recordings = new Subject<{sessionId: string; recording: FilmSessionRecordingState}>();

  readonly connection$: Observable<boolean> = this.connection.asObservable();
  readonly sessionLive$: Observable<FilmSession> = this.sessionLive.asObservable();
  readonly sessionEnded$: Observable<FilmSession> = this.sessionEnded.asObservable();
  readonly state$: Observable<FilmPlaybackState> = this.state.asObservable();
  readonly heartbeat$: Observable<FilmPlaybackHeartbeat> = this.heartbeats.asObservable();
  readonly presence$: Observable<{sessionId: string; participants: FilmParticipant[]}> = this.presence.asObservable();
  readonly readyCount$: Observable<FilmReadyCount> = this.readyCounts.asObservable();
  readonly recall$: Observable<{sessionId: string; by: string}> = this.recalls.asObservable();
  readonly removed$: Observable<{sessionId: string; userid: string}> = this.removals.asObservable();
  readonly mediaCapabilities$: Observable<FilmMediaCapabilities> = this.mediaCapabilities.asObservable();
  readonly floor$: Observable<{sessionId: string; open: boolean}> = this.floors.asObservable();
  readonly layer$: Observable<FilmDrawingLayerChange> = this.layers.asObservable();
  readonly stroke$: Observable<FilmStroke> = this.strokes.asObservable();
  readonly pointer$: Observable<FilmPointer> = this.pointers.asObservable();
  /** A coach started or stopped recording the session. */
  readonly recording$: Observable<{sessionId: string; recording: FilmSessionRecordingState}> = this.recordings.asObservable();

  constructor(private localStorageService: LocalStorageService) {
    // Signing out clears the stored user, which closes the socket.
    this.subscriptions.add(this.localStorageService.listenForUserUpdate().subscribe(user => this.sync(user)));
    this.sync(this.localStorageService.getUser());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.disconnect();
  }

  get connected(): boolean {
    return !!this.socket?.connected;
  }

  /** Opens the socket for a session room even if the cached user predates the team's flag being turned on. */
  ensureConnected(): void {
    const user = this.localStorageService.getUser();
    if (user && this.localStorageService.getToken() && !this.socket) this.connect(user);
  }

  private sync(user: User): void {
    const enabled = !!user && !!user.team?.liveFilmSessionsEnabled && !!this.localStorageService.getToken();
    if (!enabled) {
      // Signed out, or switched to a team without the feature: close it. A same-team socket opened for a
      // session room (ensureConnected) stays open.
      if (!user || this.connectionKey !== this.keyFor(user)) this.disconnect();
      return;
    }
    if (this.socket && this.connectionKey === this.keyFor(user)) return;
    this.connect(user);
  }

  private keyFor(user: User): string {
    return `${user?._id}:${user?.currentUserRole?.teamid}`;
  }

  private connect(user: User): void {
    this.disconnect();
    this.connectionKey = this.keyFor(user);
    const socket = io(`${environment.socketUrl}/film`, {
      transports: ['websocket'],
      // Read on every (re)connect, so a refreshed token is always used.
      auth: callback => callback({ token: this.localStorageService.getToken() }),
    });
    socket.on('connect', () => this.connection.next(true));
    socket.on('disconnect', () => this.connection.next(false));
    socket.on('session:live', (session: FilmSession) => this.sessionLive.next(session));
    socket.on('session:ended', (session: FilmSession) => this.sessionEnded.next(session));
    socket.on('sync:state', (state: FilmPlaybackState) => this.state.next(state));
    socket.on('sync:heartbeat', (heartbeat: FilmPlaybackHeartbeat) => this.heartbeats.next(heartbeat));
    socket.on('presence', (presence: {sessionId: string; participants: FilmParticipant[]}) => this.presence.next(presence));
    socket.on('sync:ready-count', (count: FilmReadyCount) => this.readyCounts.next(count));
    socket.on('sync:recall', (recall: {sessionId: string; by: string}) => this.recalls.next(recall));
    socket.on('participant:removed', (removal: {sessionId: string; userid: string}) => this.removals.next(removal));
    socket.on('media:capabilities', (change: FilmMediaCapabilities) => this.mediaCapabilities.next(change));
    socket.on('media:floor', (floor: {sessionId: string; open: boolean}) => this.floors.next(floor));
    socket.on('draw:layer', (change: FilmDrawingLayerChange) => this.layers.next(change));
    socket.on('draw:stroke', (stroke: FilmStroke) => this.strokes.next(stroke));
    socket.on('pointer', (pointer: FilmPointer) => this.pointers.next(pointer));
    socket.on('recording:state', (change: {sessionId: string; recording: FilmSessionRecordingState}) => this.recordings.next(change));
    this.socket = socket;
  }

  private disconnect(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.connectionKey = null;
    this.connection.next(false);
  }

  private async request<T extends FilmSocketAck>(eventName: string, payload?: any): Promise<T> {
    if (!this.socket?.connected) return { success: false, message: 'Not connected to the film session. Reconnecting…' } as T;
    try {
      return await this.socket.timeout(ACK_TIMEOUT_MS).emitWithAck(eventName, payload);
    }
    catch {
      return { success: false, message: 'The film session did not respond. Please try again.' } as T;
    }
  }

  joinRoom(sessionId: string): Promise<FilmRoomJoin> {
    return this.request('session:join', { sessionId });
  }

  leaveRoom(): Promise<FilmSocketAck> {
    return this.request('session:leave');
  }

  ping(): Promise<FilmSocketAck & {serverNow?: number}> {
    return this.request('sync:ping');
  }

  load(request: FilmLoadRequest): Promise<FilmStateAck> {
    return this.request('sync:load', request);
  }

  /** `at` is when `mediaTime` was read, in server time, so followers don't lag by the network delay. */
  transport(transport: {playing: boolean; mediaTime: number; rate: number; at?: number}): Promise<FilmStateAck> {
    return this.request('sync:transport', transport);
  }

  /** Fire-and-forget while playing; a dropped heartbeat is replaced a second later. */
  heartbeat(transport: {playing: boolean; mediaTime: number; rate: number; at?: number}): void {
    if (this.socket?.connected) this.socket.volatile.emit('sync:heartbeat', transport);
  }

  ready(version: number): Promise<FilmSocketAck> {
    return this.request('sync:ready', { version });
  }

  takeControl(): Promise<FilmStateAck> {
    return this.request('sync:take-control');
  }

  setFollowLock(locked: boolean): Promise<FilmStateAck> {
    return this.request('sync:follow-lock', { locked });
  }

  recall(): Promise<FilmSocketAck> {
    return this.request('sync:recall');
  }

  /**
   * The presenter's drawing on the frame they're paused on. `fromSaved` marks a group they had saved before the
   * session, and `edited` a saved group they changed.
   */
  drawLayer(layer: {group: Group; mediaTime: number; fromSaved?: boolean; edited?: boolean}): Promise<FilmLayerAck> {
    return this.request('draw:layer', layer);
  }

  /** Fire-and-forget: the shape the presenter is drawing right now, or null once it's done. */
  drawStroke(shape: FilmDrawingShape | null): void {
    if (this.socket?.connected) this.socket.volatile.emit('draw:stroke', { shape });
  }

  /** Fire-and-forget: a coach's laser pointer, normalized to the video. */
  pointerMove(x: number, y: number): void {
    if (this.socket?.connected) this.socket.volatile.emit('pointer:move', { x, y });
  }

  pointerHide(): void {
    if (this.socket?.connected) this.socket.volatile.emit('pointer:hide');
  }

  raiseHand(up: boolean): Promise<FilmSocketAck> {
    return this.request('hand:raise', { up });
  }

  lowerHand(userid: User['_id']): Promise<FilmSocketAck> {
    return this.request('hand:lower', { userid });
  }

  lowerAllHands(): Promise<FilmSocketAck> {
    return this.request('hand:lower-all');
  }
}
